export const STAGE2_CONDUIT_ROOT_CAPTURE_WINDOW_MS = 180 * 1000;
export const STAGE2_CONDUIT_CLIPBOARD_CLEANUP_MARGIN_MS = 30 * 1000;
export const STAGE2_CONDUIT_PORTAL_ARM_WINDOW_MS =
  STAGE2_CONDUIT_ROOT_CAPTURE_WINDOW_MS - STAGE2_CONDUIT_CLIPBOARD_CLEANUP_MARGIN_MS;

export type Stage2ConduitArmStatus =
  | 'unavailable'
  | 'available'
  | 'begun'
  | 'acknowledged'
  | 'consumed';

export interface Stage2ConduitArmSnapshot {
  status: Stage2ConduitArmStatus;
  usableUntilMs: number | null;
}

export type Stage2ConduitClipboardOutcome =
  | 'copied'
  | 'denied'
  | 'late-cleared'
  | 'late-clear-failed';
export type Stage2ConduitConfirmationOutcome = 'confirmed' | 'cancelled' | 'denied';
export type Stage2ConduitMintOutcome<T> =
  | Readonly<{ status: 'ready'; value: T }>
  | Readonly<{ status: 'expired' }>;

type Stage2Clock = () => number;

const snapshot = (
  status: Stage2ConduitArmStatus,
  usableUntilMs: number | null = null,
): Stage2ConduitArmSnapshot => Object.freeze({ status, usableUntilMs });

/**
 * One fresh bootstrap permits one arm attempt. The portal deadline is fixed
 * before the operator starts the root helper and ends early enough to leave a
 * cleanup margin inside the helper's authoritative 180-second capture window.
 */
export class Stage2ConduitArmAttempt {
  private status: Stage2ConduitArmStatus = 'unavailable';
  private usableUntilMs: number | null = null;

  constructor(private readonly clock: Stage2Clock = Date.now) {}

  private now(): number | null {
    const value = this.clock();
    return Number.isSafeInteger(value) && value >= 0 ? value : null;
  }

  private expireIfNeeded(): void {
    if (this.status !== 'begun' && this.status !== 'acknowledged') return;
    const now = this.now();
    if (now === null || this.usableUntilMs === null || now >= this.usableUntilMs) {
      this.consume();
    }
  }

  state(): Stage2ConduitArmSnapshot {
    this.expireIfNeeded();
    return snapshot(this.status, this.usableUntilMs);
  }

  resetForFreshBootstrap(): Stage2ConduitArmSnapshot {
    this.status = 'available';
    this.usableUntilMs = null;
    return snapshot(this.status);
  }

  invalidate(): Stage2ConduitArmSnapshot {
    this.status = 'unavailable';
    this.usableUntilMs = null;
    return snapshot(this.status);
  }

  projectionMutated(): Stage2ConduitArmSnapshot {
    this.expireIfNeeded();
    if (this.status === 'begun' || this.status === 'acknowledged') {
      return this.consume();
    }
    return snapshot(this.status, this.usableUntilMs);
  }

  denied(): Stage2ConduitArmSnapshot {
    return this.consume();
  }

  begin(): Stage2ConduitArmSnapshot | null {
    if (this.status !== 'available') return null;
    const now = this.now();
    if (now === null) {
      this.consume();
      return null;
    }
    const usableUntilMs = now + STAGE2_CONDUIT_PORTAL_ARM_WINDOW_MS;
    if (!Number.isSafeInteger(usableUntilMs) || usableUntilMs <= now) {
      this.consume();
      return null;
    }
    this.status = 'begun';
    this.usableUntilMs = usableUntilMs;
    return snapshot(this.status, this.usableUntilMs);
  }

  acknowledgeArmed(): Stage2ConduitArmSnapshot | null {
    this.expireIfNeeded();
    if (this.status !== 'begun' || this.usableUntilMs === null) return null;
    this.status = 'acknowledged';
    return snapshot(this.status, this.usableUntilMs);
  }

  canUse(): boolean {
    this.expireIfNeeded();
    return this.status === 'acknowledged' && this.usableUntilMs !== null;
  }

  consume(): Stage2ConduitArmSnapshot {
    this.status = 'consumed';
    this.usableUntilMs = null;
    return snapshot(this.status);
  }
}

export function confirmStage2ConduitArmAttempt(
  attempt: Stage2ConduitArmAttempt,
  confirm: () => boolean,
): Stage2ConduitConfirmationOutcome {
  if (!attempt.canUse()) {
    attempt.consume();
    return 'denied';
  }
  if (!confirm()) return 'cancelled';
  return attempt.canUse() ? 'confirmed' : 'denied';
}

export async function mintStage2ConduitPayloadWhileArmed<T>(
  attempt: Stage2ConduitArmAttempt,
  mint: () => Promise<T>,
): Promise<Stage2ConduitMintOutcome<T>> {
  if (!attempt.canUse()) {
    attempt.consume();
    return Object.freeze({ status: 'expired' });
  }
  const value = await mint();
  if (!attempt.canUse()) return Object.freeze({ status: 'expired' });
  return Object.freeze({ status: 'ready', value });
}

/**
 * The payload is written at most once. A write that completes after the fixed
 * portal cutoff (or after its request ticket becomes stale) is immediately
 * overwritten with an empty clipboard value and can never report success.
 */
export async function writeStage2ConduitBrokerPayloadOnce(
  payload: string,
  attempt: Stage2ConduitArmAttempt,
  writeText: (value: string) => Promise<void>,
  isTicketCurrent: () => boolean,
): Promise<Stage2ConduitClipboardOutcome> {
  if (!isTicketCurrent() || !attempt.canUse()) {
    attempt.consume();
    return 'denied';
  }

  await writeText(payload);
  if (!isTicketCurrent() || !attempt.canUse()) {
    attempt.consume();
    try {
      await writeText('');
    } catch {
      // The caller reports one urgent constant warning. Never include the
      // clipboard implementation's exception or the bearer in an error.
      return 'late-clear-failed';
    }
    return 'late-cleared';
  }

  attempt.consume();
  return 'copied';
}
